/**
 * 运营-分账监控（Tab 式：分账流水 / 银行流水）
 * 串起 F3（分账）+ F4（双票代开）+ F5（退款，融在订单状态里）
 */

import { useState } from 'react'
import { Card, Table, Button, Badge, useApi, money, time } from '../../components/ui'
import { OrderDetailModal } from '../../components/OrderDetailModal'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

type Tab = 'settle' | 'bank'

export function AdminSettlements() {
  const [tab, setTab] = useState<Tab>('settle')

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>分账监控</h2>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <Button size="sm" variant={tab === 'settle' ? 'primary' : 'default'} onClick={() => setTab('settle')}>
          分账流水
        </Button>
        <Button size="sm" variant={tab === 'bank' ? 'primary' : 'default'} onClick={() => setTab('bank')}>
          银行流水
        </Button>
      </div>

      {tab === 'settle' && <SettleTab />}
      {tab === 'bank' && <BankTab />}
    </div>
  )
}

// ============ Tab 1：分账流水 ============

interface Order {
  order_id: string
  amount_total: string
  amount_refunded: string
  status: string
  seller_id: string
  factory_id: string
  confirm_time: string | null
  logistics_no: string | null
}
interface Settlement {
  settlement_id: string
  order_id: string
  seller_amount: string
  factory_amount_net: string
  platform_fee: string
  status: string
  bank_serial_no: string
  remark_seller: string
  remark_factory: string
  executed_at: string | null
}

function SettleTab() {
  const orders = useApi<Order[]>(async () => api.get('/orders'), [])
  const settlements = useApi<Settlement[]>(async () => api.get('/admin/settlements'), [])
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const [okMsg, setOkMsg] = useState('')
  const [orderDetail, setOrderDetail] = useState<string | null>(null)

  const paidOrders = (orders.data ?? []).filter((o) => o.status === 'PAID')
  const refundedOrders = (orders.data ?? []).filter((o) => o.status === 'REFUNDED')

  async function settle(orderId: string) {
    setBusy(orderId); setErr('')
    try {
      const r = await api.post<{ auto_invoiced?: boolean }>(`/settlements/${orderId}/settle`)
      setOkMsg(`✓ 订单 ${orderId} 分账成功${r.auto_invoiced ? '，已自动代开双票' : ''}`)
      orders.refresh(); settlements.refresh()
    } catch (e) { setErr((e as Error).message) } finally { setBusy('') }
  }
  async function issueInvoice(orderId: string) {
    setBusy('inv-' + orderId); setErr('')
    try { await api.post(`/invoices/${orderId}/issue`); orders.refresh() }
    catch (e) { setErr((e as Error).message) } finally { setBusy('') }
  }
  async function redFlush(orderId: string) {
    if (!confirm('确定双票红冲（退货）？商品票 + 服务费票将被冲销。')) return
    setBusy('red-' + orderId); setErr('')
    try { await api.post(`/invoices/${orderId}/red-flush`); orders.refresh() }
    catch (e) { setErr((e as Error).message) } finally { setBusy('') }
  }
  async function reverse(settlementId: string) {
    if (!confirm('确定反向分账（退货）？资金将从子户扣回汇总户。')) return
    setBusy('rev-' + settlementId); setErr('')
    try { await api.post(`/admin/settlements/${settlementId}/reverse`); settlements.refresh() }
    catch (e) { setErr((e as Error).message) } finally { setBusy('') }
  }

  // 模拟电商同步：某笔订单退款了
  async function syncRefund() {
    setBusy('sync-refund'); setErr(''); setOkMsg('')
    try {
      const r = await api.post<{ order: Order; event: string }>('/orders/sync', { simulate: 'refund' })
      setOkMsg(`✓ 订单 ${r.order.order_id} 已同步退款状态：${r.event}（${money(r.order.amount_refunded)}）`)
      orders.refresh(); settlements.refresh()
    } catch (e) { setErr((e as Error).message) } finally { setBusy('') }
  }

  return (
    <div>
      {(err || okMsg) && (
        <div style={{ marginBottom: 12, color: err ? '#dc2626' : '#16a34a', fontSize: 13 }}>{err || okMsg}</div>
      )}

      <Card title="订单同步（INT-A）" action={<Button size="sm" onClick={syncRefund} disabled={busy === 'sync-refund'}>{busy === 'sync-refund' ? '同步中...' : '模拟同步：订单退款'}</Button>}>
        <div style={{ fontSize: 13, color: '#888' }}>
          退款不是独立操作——同步订单时，如果电商那边的订单状态变成已退款，平台按状态自动处理：未开票的直接退款作废，已开票的自动反向分账+红冲。
        </div>
      </Card>

      <Card title={`已支付订单（PAID，${paidOrders.length}）`}>
        <Table<Order>
          rows={paidOrders}
          empty="无已支付订单（分账和开票在确认收货后自动完成；此处可手动兜底）"
          columns={[
            { key: 'order_id', title: '订单号', render: (r) => <a onClick={() => setOrderDetail(r.order_id)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
            { key: 'amount_total', title: '金额', render: (r) => money(r.amount_total) },
            { key: 'logistics_no', title: '物流', render: (r) => r.logistics_no ? '✓' : '⚠️ 未回传' },
            { key: 'confirm_time', title: '确认收货', render: (r) => (r.confirm_time ? time(r.confirm_time) : '未确认') },
            {
              key: 'action', title: '操作',
              render: (r) => (
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button size="sm" onClick={() => settle(r.order_id)} disabled={busy === r.order_id}>
                    {busy === r.order_id ? '分账中...' : '手动分账'}
                  </Button>
                  <Button size="sm" variant="default" onClick={() => issueInvoice(r.order_id)} disabled={busy === 'inv-' + r.order_id}>
                    {busy === 'inv-' + r.order_id ? '开票中...' : '手动开票'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => redFlush(r.order_id)} disabled={busy === 'red-' + r.order_id}>
                    红冲
                  </Button>
                </div>
              ),
            },
          ]}
        />
        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
          分账成功后自动代开双票（商品票13%+服务费票6%）。手动按钮用于兜底/补开/红冲。
        </div>
      </Card>

      <Card title={`分账流水（${settlements.data?.length ?? 0}）`}>
        <Table<Settlement>
          rows={settlements.data ?? []}
          empty="无分账记录"
          columns={[
            { key: 'settlement_id', title: '分账ID' },
            { key: 'order_id', title: '订单', render: (r) => <a onClick={() => setOrderDetail(r.order_id)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
            { key: 'seller_amount', title: '商家服务费', render: (r) => money(r.seller_amount) },
            { key: 'factory_amount_net', title: '工厂实收', render: (r) => money(r.factory_amount_net) },
            { key: 'platform_fee', title: '平台抽佣', render: (r) => money(r.platform_fee) },
            { key: 'remark_factory', title: '货款备注', render: (r) => <code>{r.remark_factory}</code> },
            { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            {
              key: 'action', title: '操作',
              render: (r) =>
                r.status === 'SUCCESS' ? (
                  <Button size="sm" variant="danger" onClick={() => reverse(r.settlement_id)} disabled={busy === 'rev-' + r.settlement_id}>
                    反向分账
                  </Button>
                ) : (
                  <span style={{ color: '#888' }}>-</span>
                ),
            },
          ]}
        />
      </Card>

      {refundedOrders.length > 0 && (
        <Card title={`已退款订单（${refundedOrders.length}）`}>
          <Table<Order>
            rows={refundedOrders}
            empty="无退款订单"
            columns={[
              { key: 'order_id', title: '订单号', render: (r) => <a onClick={() => setOrderDetail(r.order_id)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
              { key: 'amount_total', title: '订单金额', render: (r) => money(r.amount_total) },
              { key: 'amount_refunded', title: '退款金额', render: (r) => money(r.amount_refunded) },
              { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            ]}
          />
          <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
            退款同步来自电商平台。已开票的订单退款时已自动反向分账+红冲；未开票的直接退款作废。
          </div>
        </Card>
      )}

      <HelpBubble pageKey="admin-settlements" title="分账监控">
        <p>分账和开票是自动的，本页用于<b>监控和兜底</b>。可手动触发分账/开票/红冲（幂等，已处理则跳过）。</p>
        <p>点「模拟同步：订单退款」测试退款链路——已开票的会自动反向分账+红冲。银行流水 Tab 查当日资金流水。</p>
      </HelpBubble>

      {orderDetail && <OrderDetailModal orderId={orderDetail} onClose={() => setOrderDetail(null)} />}
    </div>
  )
}

// ============ Tab 2：银行流水（INT-B getTransactions） ============

interface TxRow {
  serial_no: string
  seller: string
  factory: string
  platform_fee: string
  remark_seller: string
  remark_factory: string
  ts: string
}

function BankTab() {
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(today)
  const data = useApi<{ date: string; transactions: TxRow[] }>(async () => api.get(`/settlements/bank/transactions?date=${date}`), [date])

  return (
    <div>
      <Card title="银行流水（INT-B）">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13 }}>日期：</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4 }}
          />
        </div>
        <Table<TxRow>
          rows={data.data?.transactions ?? []}
          empty={data.loading ? '加载中...' : `该日无银行流水（${data.data?.date ?? date}）`}
          columns={[
            { key: 'serial_no', title: '流水号' },
            { key: 'seller', title: '商家服务费', render: (r) => money(r.seller) },
            { key: 'factory', title: '工厂货款', render: (r) => money(r.factory) },
            { key: 'platform_fee', title: '平台抽佣', render: (r) => money(r.platform_fee) },
            { key: 'remark_seller', title: '服务费备注', render: (r) => <code>{r.remark_seller}</code> },
            { key: 'remark_factory', title: '货款备注', render: (r) => <code>{r.remark_factory}</code> },
            { key: 'ts', title: '时间', render: (r) => time(r.ts) },
          ]}
        />
        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
          mock：按当日成功的分账/反向分账记录反推银行流水（真实场景调银行 getTransactions 接口）。
        </div>
      </Card>
    </div>
  )
}
